import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Copy, Download, Search, X } from 'lucide-react';
import { SlideOver } from '../common';
import { useUserStore } from '../../store/userStore';
import { buildLeadLink, copyText, qrFileName, type LeadLinkKind } from '../../lib/leadLinks';
import { QR_EXPORT_SIZE, drawQrToCanvas, qrPngDataUrl } from '../../lib/qr';
import type { LeadSource } from '../../types/lead';
import type { UserListItem } from '../../types';

/**
 * Link + QR generator for one event source (CRM-130).
 *
 * Replaces the hand-kept spreadsheet of `/lead/<repUserId>?source=<code>` URLs:
 * nobody should be copying internal user ids by hand, and the QR is what
 * actually gets printed for a booth.
 */
const PREVIEW_SIZE = 176;

const KINDS: { value: LeadLinkKind; label: string; hint: string }[] = [
  { value: 'REP_SOURCE', label: 'Rep + event', hint: 'Tagged to this event and attributed to the rep.' },
  { value: 'SOURCE_ONLY', label: 'Event only', hint: 'No rep attribution — for the website or a shared handout.' },
  { value: 'REP_ONLY', label: 'Rep only', hint: 'The coach picks the event on the form.' },
];

const userLabel = (u: UserListItem) => u.displayName ?? u.email;

function RepPicker({
  users,
  value,
  onChange,
}: {
  users: UserListItem[];
  value: UserListItem | null;
  onChange: (user: UserListItem | null) => void;
}) {
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 8);
    return users
      .filter((u) => userLabel(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, query]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600">
        <span className="min-w-0 truncate text-sm text-gray-900 dark:text-gray-100">
          {userLabel(value)}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Clear rep"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900"
          placeholder="Search reps…"
          aria-label="Search reps"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <ul className="mt-1 max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
        {matches.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => onChange(u)}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50
                         dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {userLabel(u)}
              {u.displayName && <span className="ml-2 text-xs text-gray-400">{u.email}</span>}
            </button>
          </li>
        ))}
        {matches.length === 0 && (
          <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching rep.</li>
        )}
      </ul>
    </div>
  );
}

export function SourceLinkPanel({
  source,
  isOpen,
  onClose,
}: {
  source: LeadSource | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const users = useUserStore((s) => s.users);
  const fetchUsers = useUserStore((s) => s.fetchUsers);
  const [kind, setKind] = useState<LeadLinkKind>('REP_SOURCE');
  const [rep, setRep] = useState<UserListItem | null>(null);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (isOpen) fetchUsers();
  }, [isOpen, fetchUsers]);

  const link = source
    ? buildLeadLink(window.location.origin, kind, { repUserId: rep?.id, sourceCode: source.code })
    : null;

  useEffect(() => {
    setCopied(false);
    const canvas = canvasRef.current;
    if (!canvas || !link) return;
    // Draws resolve in call order (the logo is loaded once and cached), so the
    // last link the user picked is the one left on the canvas.
    void drawQrToCanvas(canvas, link, PREVIEW_SIZE);
  }, [link]);

  if (!source) return null;

  const needsRep = kind !== 'SOURCE_ONLY';

  const onCopy = async () => {
    if (!link) return;
    if (await copyText(link)) {
      setCopied(true);
      toast.success('Link copied');
    } else {
      toast.error('Could not copy — select the link and copy it manually');
    }
  };

  const onDownload = async () => {
    if (!link) return;
    let href: string;
    try {
      href = await qrPngDataUrl(link, QR_EXPORT_SIZE);
    } catch {
      toast.error('Could not build the QR image — reload and try again');
      return;
    }
    const a = document.createElement('a');
    a.href = href;
    a.download = qrFileName([source.code, kind === 'SOURCE_ONLY' ? null : rep?.displayName ?? rep?.email]);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title={`Link for ${source.label}`}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => setKind(k.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                kind === k.value
                  ? 'bg-blast-50 text-blast-700 dark:bg-blast-950 dark:text-blast-300'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {KINDS.find((k) => k.value === kind)?.hint}
        </p>

        {needsRep && (
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Rep</span>
            <RepPicker users={users} value={rep} onChange={setRep} />
          </div>
        )}

        {link ? (
          <>
            <div className="flex items-center gap-2">
              <input
                readOnly
                aria-label="Lead form link"
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm
                           dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={onCopy}
                className="flex flex-shrink-0 items-center gap-1 rounded-md bg-blast-600 px-3 py-2 text-sm font-semibold text-white"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="flex flex-col items-center gap-2">
              <canvas
                ref={canvasRef}
                role="img"
                aria-label="QR code for the lead form link"
                className="rounded-md border border-gray-200 dark:border-gray-700"
                style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
              />
              <button
                type="button"
                onClick={onDownload}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium
                           text-gray-700 dark:border-gray-600 dark:text-gray-200"
              >
                <Download className="h-4 w-4" /> Download QR ({QR_EXPORT_SIZE}px PNG)
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Pick a rep to build the link.</p>
        )}
      </div>
    </SlideOver>
  );
}
